/**
 * Maps the UserSearchPanel's string filter keys (from userFiltersConfig) to the
 * server-side query params the /users endpoint expects (comma-separated numeric
 * enum values, plus the interview-topic string keys).
 *
 * The backend does NOT support filtering by online/availability, so that
 * dimension is intentionally omitted (the panel no longer exposes it).
 */

import {
  FetchManyUsersDto,
  ProfileGender,
  OnboardingStep,
  AccountStatus,
  MembershipType,
  UserType,
  INTERVIEW_TOPIC,
} from 'src/store/userApi';
import type { UserPanelFilters } from './UserSearchPanel';

const genderMap: Record<string, number> = {
  female: ProfileGender.FEMALE,
  male: ProfileGender.MALE,
  nonbinary: ProfileGender.NON_BINARY,
};

const stepMap: Record<string, number> = {
  start: OnboardingStep.START,
  ready_interview: OnboardingStep.READY_FOR_INTERVIEW,
  interview: OnboardingStep.INTERVIEW,
  upload_photo: OnboardingStep.UPLOAD_PHOTO,
  plan_comparison: OnboardingStep.PLAN_COMPARISON,
  subscription: OnboardingStep.SUBSCRIPTION,
  review_profile: OnboardingStep.REVIEW_PROFILE,
  completed: OnboardingStep.COMPLETED,
};

const statusMap: Record<string, number> = {
  active: AccountStatus.ACTIVE,
  blocked: AccountStatus.BLOCKED,
  paused: AccountStatus.PAUSED,
  skip_match: AccountStatus.SKIP_MATCH,
};

const planMap: Record<string, number> = {
  freemium: MembershipType.FREEMIUM,
  premium_trial: MembershipType.PREMIUM_TRIAL,
  premium: MembershipType.PREMIUM,
};

const typeMap: Record<string, number> = {
  normal: UserType.NORMAL,
  sample: UserType.SAMPLE,
  test: UserType.TEST,
};

// 'not_started' → the backend's 'none' sentinel (currentTopic IS NULL).
const topicMap: Record<string, string> = {
  not_started: 'none',
  init: INTERVIEW_TOPIC.INIT,
  lifestyle: INTERVIEW_TOPIC.LIFE_STYLE,
  family: INTERVIEW_TOPIC.FAMILY,
  relationship: INTERVIEW_TOPIC.RELATIONSHIP,
};

function toCsv(
  vals: string[] | undefined,
  map: Record<string, number | string>
): string | undefined {
  if (!vals || vals.length === 0) return undefined;
  const mapped = vals
    .map((v) => map[v])
    .filter((v) => v !== undefined)
    .map(String);
  return mapped.length > 0 ? mapped.join(',') : undefined;
}

/**
 * Build the /users query DTO from the panel's filter state + pagination window.
 */
export function buildUsersQuery(
  filters: UserPanelFilters,
  offset: number,
  limit: number
): FetchManyUsersDto {
  const { search, selections, ageMin, ageMax, includeDeleted } = filters;
  const dto: FetchManyUsersDto = { limit, offset };

  if (search.trim()) dto.search = search.trim();

  const gender = toCsv(selections.gender, genderMap);
  if (gender) dto.gender = gender;

  const onboardingStep = toCsv(selections.step, stepMap);
  if (onboardingStep) dto.onboardingStep = onboardingStep;

  const accountStatus = toCsv(selections.status, statusMap);
  if (accountStatus) dto.accountStatus = accountStatus;

  const membershipType = toCsv(selections.plan, planMap);
  if (membershipType) dto.membershipType = membershipType;

  const userType = toCsv(selections.type, typeMap);
  if (userType) dto.userType = userType;

  const interviewTopic = toCsv(selections.topic, topicMap);
  if (interviewTopic) dto.interviewTopic = interviewTopic;

  if (ageMin.trim()) dto.ageMin = ageMin.trim();
  if (ageMax.trim()) dto.ageMax = ageMax.trim();

  if (includeDeleted) dto.includeDeleted = true;

  return dto;
}
